import _ from 'lodash';
import { useSelector } from 'react-redux';
import { CoverageChart } from '../../_global/charts/coverage.chart';
import { CoverageChartV6 } from '../../_global/charts/coverage.chart.v6';
import { calculateCoverageOptions } from '../../_statics/coverage.statics';
import { calculateCoverageOptionsV6 } from '../../_statics/coverage.statics.v6';
import { BoostRaterVersionResponse } from '../../workflows/quotes/prime-100/api/types';
import { resolveP100RaterVersionFromEntity } from '../../workflows/quotes/prime-100/utils';
import { CoverageSubValue } from './P100CoveragesReadOnly/CoverageSubValue';

const isV6 = (version: string | undefined) =>
  version === BoostRaterVersionResponse.V6 ||
  (typeof version === 'string' && version.toUpperCase() === 'V6');

/**
 * A P100 record's coverage options under its rater version: the list the read-only chart draws, and the broker look's
 * coverage tables read.
 */
export function p100CoverageOptions(
  data: any,
  {
    P_100,
    state,
    boostRaterVersion: boostRaterVersionProp,
  }: { P_100: any; state?: string; boostRaterVersion?: string }
) {
  const boostRaterVersion =
    boostRaterVersionProp ?? resolveP100RaterVersionFromEntity(data);
  const useV6 = isV6(boostRaterVersion);
  const coverageOptions = useV6
    ? calculateCoverageOptionsV6(data, {
        onChange: null,
        register: null,
        coverageMapping: P_100,
        boostVersion: boostRaterVersion,
      })
    : calculateCoverageOptions(data, {
        onChange: null,
        register: null,
        onClick: null,
        disableSocial: !data.socialEngEndorsement,
        disableRansom: !data.ransomPaymentEndorsement,
        socialEngEndorsement: data.socialEngEndorsement,
        socialReason: !_.isEmpty(P_100) && P_100?.SOCIAL_ENGINEERING?.tooltip,
        disableWebsite: !data.websiteMediaContentLiabilityEndorsement,
        coverageMapping: P_100,
        state,
        boostVersion: boostRaterVersion,
      });
  return { coverageOptions, useV6 };
}

export const P100CoveragesReadOnly = ({
  data,
  state,
  boostRaterVersion,
}: any) => {
  const P_100 = useSelector(({ auth }) => auth.P_100);
  const { coverageOptions, useV6 } = p100CoverageOptions(data, {
    P_100,
    state,
    boostRaterVersion,
  });

  const {
    businessIncomeCoverage,
    ransomPaymentLimit,
    contingentBusinessIncomeSecurityFailureEndorsement,
    contingentBusinessIncomeSystemFailureItEndorsement,
    socialEngLimit,
    socialEngDeductible,
    socialEngEndorsement,
    ransomPaymentEndorsement,
    websiteMediaContentLiabilityEndorsement,
    websiteMediaContentLiabilitySubLimit,
    websiteMediaContentLiabilityDeductible,
    reverseSocialEngineeringEndorsement,
    reverseSocialEngineeringLimit,
    reverseSocialEngineeringDeductible,
    enhancedBusinessAndCbi,
    enhancedBusinessAndCbiWithSystemFailure,
    fundsTransferFraudCoverageLimit,
    fundsTransferFraudCoverageDeductible,
    fundsTransferFraudAndSocialEngineeringEndorsement,
    fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement,
    fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement,
    invoiceManipulationEndorsement,
    invoiceManipulationDeductible,
  } = data;
  const excludedFromSE = _.get(data, 'firmographicData.excludedFromSE', false);

  if (useV6) {
    return (
      <CoverageChartV6
        options={coverageOptions}
        readOnly
        data={data}
        renderContigentBusinessIncomeSF={() =>
          contingentBusinessIncomeSecurityFailureEndorsement && (
            <CoverageSubValue label="Contingent Business Income - Security Failure" />
          )
        }
        renderContigentBusinessIncomeSFIT={() =>
          contingentBusinessIncomeSystemFailureItEndorsement && (
            <CoverageSubValue label="Contingent Business Income - System Failure (IT)" />
          )
        }
        renderFtfSeSubValues={() =>
          fundsTransferFraudAndSocialEngineeringEndorsement && (
            <>
              {fundsTransferFraudCoverageLimit && (
                <CoverageSubValue
                  label="Selected Sublimit"
                  value={fundsTransferFraudCoverageLimit}
                />
              )}
              {fundsTransferFraudCoverageDeductible != null && (
                <CoverageSubValue
                  label="Selected Deductible"
                  value={fundsTransferFraudCoverageDeductible}
                />
              )}
            </>
          )
        }
        renderFtfSeCpSubValues={() =>
          fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement && (
            <>
              {fundsTransferFraudCoverageLimit && (
                <CoverageSubValue
                  label="Selected Sublimit"
                  value={fundsTransferFraudCoverageLimit}
                />
              )}
              {fundsTransferFraudCoverageDeductible != null && (
                <CoverageSubValue
                  label="Selected Deductible"
                  value={fundsTransferFraudCoverageDeductible}
                />
              )}
            </>
          )
        }
        renderfundsTransferHeldFundsValues={() =>
          fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement && (
            <CoverageSubValue label="Enhancement to Include Client Held Funds" />
          )
        }
        renderinvoiceManipulationDeductibleValues={() =>
          invoiceManipulationEndorsement && (
            <CoverageSubValue
              label="Deductible"
              value={invoiceManipulationDeductible}
            />
          )
        }
        renderWebsiteMediaDeductibleValues={() =>
          websiteMediaContentLiabilityEndorsement && (
            <CoverageSubValue
              label="Deductible"
              value={websiteMediaContentLiabilityDeductible}
            />
          )
        }
      />
    );
  }

  return (
    <CoverageChart
      options={coverageOptions}
      readOnly
      social={socialEngEndorsement}
      ransom={ransomPaymentEndorsement}
      exclude={excludedFromSE}
      website={websiteMediaContentLiabilityEndorsement}
      data={data}
      renderSublimit={() => (
        <CoverageSubValue
          label="Selected Sublimit"
          value={businessIncomeCoverage}
        />
      )}
      renderRansomLimit={() =>
        ransomPaymentEndorsement && (
          <CoverageSubValue
            label="Selected Sublimit"
            value={ransomPaymentLimit}
          />
        )
      }
      renderSocialLimit={() =>
        socialEngEndorsement && (
          <CoverageSubValue label="Selected Sublimit" value={socialEngLimit} />
        )
      }
      renderSocialDeductible={() =>
        socialEngEndorsement && (
          <CoverageSubValue
            label="Selected Deductible"
            value={socialEngDeductible}
          />
        )
      }
      renderReverseSocialEngineeringLimit={() =>
        reverseSocialEngineeringEndorsement && (
          <CoverageSubValue
            label="Selected Sublimit"
            value={reverseSocialEngineeringLimit}
          />
        )
      }
      renderReverseSocialEngineeringDeductible={() =>
        reverseSocialEngineeringEndorsement && (
          <CoverageSubValue
            label="Selected Deductible"
            value={reverseSocialEngineeringDeductible}
          />
        )
      }
      renderEnhancedEcommerce={() =>
        enhancedBusinessAndCbi && (
          <CoverageSubValue label="E-Commerce Enhancement" />
        )
      }
      renderEnhancedEcommerceAndSystemFailure={() =>
        enhancedBusinessAndCbiWithSystemFailure && (
          <CoverageSubValue label="E-Commerce Enhancement & System Failure" />
        )
      }
      renderWebsiteLimit={() =>
        websiteMediaContentLiabilityEndorsement && (
          <CoverageSubValue
            label="Selected Sublimit"
            value={websiteMediaContentLiabilitySubLimit}
          />
        )
      }
    />
  );
};

export default P100CoveragesReadOnly;
